import {
  ActionPlanCollaboratorSchema,
  ActionPlanCollaboratorsSchema,
  ActionPlanTaskOwnersSchema,
  GuestSchema,
} from '../../../../definitions/schemas';
import { ascendingSort } from '../../../../lib/Array';
import { CollaboratorType } from '../../../../enums/CollaboratorType';
import { findCollaborator } from '../../helper';
import { getDisplayName } from '../../../../lib/DisplayName';

export const isOwnerSelected = (
  option: ActionPlanCollaboratorSchema,
  selectedOwners: ActionPlanTaskOwnersSchema
): boolean =>
  selectedOwners.some(
    ({ ownerId, ownerType }) =>
      ownerId === option.id && ownerType === option.collaboratorType
  );

export const isCollaborator = (
  option: ActionPlanCollaboratorSchema,
  collaborators: ActionPlanCollaboratorsSchema
) => !!findCollaborator(collaborators, option.id, option.collaboratorType);

/**
 * Order the available task owner options with these rules:
 *  1. Selected contacts or user collaborators
 *  2. Non-selected contacts
 *  3. Non-selected user collaborators
 *  4. (Admin/Salespeople only) Other non-selected organization users
 * All the results are alphabetically ordered.
 */
export const getOrderedOwnerOptions = (
  options: ActionPlanCollaboratorsSchema,
  selectedOwners: ActionPlanTaskOwnersSchema,
  collaborators: ActionPlanCollaboratorsSchema
): ActionPlanCollaboratorsSchema => {
  const selected: ActionPlanCollaboratorsSchema = [];
  const contacts: ActionPlanCollaboratorsSchema = [];
  const collaboratorUsers: ActionPlanCollaboratorsSchema = [];
  const otherOrgUsers: ActionPlanCollaboratorsSchema = [];

  // not using sort because this requires less comparisons and less array traversing
  options
    .filter((option) => !!option.email)
    .sort((a, b) =>
      ascendingSort(
        getDisplayName(a, { fullEmail: true }),
        getDisplayName(b, { fullEmail: true })
      )
    )
    .forEach((option) => {
      const isSelected = isOwnerSelected(option, selectedOwners);

      if (isSelected) {
        selected.push(option);
      } else if (option.collaboratorType === CollaboratorType.Contact) {
        contacts.push(option);
      } else if (isCollaborator(option, collaborators)) {
        collaboratorUsers.push(option);
      } else {
        otherOrgUsers.push(option);
      }
    });

  return [...selected, ...contacts, ...collaboratorUsers, ...otherOrgUsers];
};

export const splitCollaboratorsByType = (
  options: ActionPlanCollaboratorsSchema
) => {
  const contacts: ActionPlanCollaboratorsSchema = [];
  const users: ActionPlanCollaboratorsSchema = [];

  options.forEach((option) => {
    const collaboratorType =
      option.collaboratorType === CollaboratorType.Contact ? contacts : users;
    collaboratorType.push(option);
  });

  return [contacts, users];
};

export const buildCollaboratorsFromGuests = (
  guests: GuestSchema[]
): ActionPlanCollaboratorsSchema =>
  guests.map(
    (participant): ActionPlanCollaboratorSchema => ({
      ...participant,
      collaboratorType: participant.type!,
    })
  );

export const filterOwnersOptionsBySearchCriteria = (
  options: ActionPlanCollaboratorsSchema,
  search: string
) => {
  const lowercasedSearch = search.trim().toLowerCase();
  if (!lowercasedSearch) {
    return options;
  }

  return options.filter(({ email, firstName, lastName }) => {
    const values = [email, firstName, lastName];

    return (
      values
        .map((v) => v?.trim().toLowerCase())
        .join(' ')
        .indexOf(lowercasedSearch) > -1
    );
  });
};
